/**
 * DecisionModal — record a decision on a Lead.
 * Extracted from LeadDetailPage.tsx (iteration 4 refactor-split).
 * Updated (landlord-compliance): replaced <select> with radio buttons;
 * added conditional rejection basis fieldset.
 */

import { useState, useRef, useEffect } from 'react';
import { Button } from '../../../../components/ui/button';
import { decideLead } from '../../../../lib/leasing.api';
import type { LandlordSelectableLeadDecision, LeadRejectionBasis } from '../../../../lib/leasing.api';
import { LANDLORD_DECISIONS, DECISION_LABELS } from '../shared-constants';

const REJECTION_BASIS_OPTIONS: { value: LeadRejectionBasis; label: string }[] = [
  { value: 'procedural_listing_closed', label: 'Listing no longer available' },
  { value: 'procedural_other_applicant', label: 'Another applicant was selected' },
  { value: 'substantive_consumer_report', label: 'Based on a background check or consumer report' },
  { value: 'substantive_employment', label: 'Based on employment or income verification' },
  { value: 'substantive_rental_history', label: 'Based on rental history from a prior landlord' },
  { value: 'substantive_other', label: 'Other reason' },
];

const DECISION_OPTION_LABELS: Record<LandlordSelectableLeadDecision, string> = {
  approved_lease_started: 'Approve — starting a lease',
  withdrawn: 'Withdraw this application',
  rejected: 'Reject applicant(s)',
};

export interface DecisionModalProps {
  leadId: string;
  onClose: () => void;
  onSuccess: (siblingCount: number, siblingIds: string[]) => void;
}

/**
 * Source clarification copy for bases where the information could be self-obtained or
 * obtained via a third-party service. Selecting "service" stores
 * substantive_consumer_report, which triggers the legally required CRA disclosure block
 * in the rejection notice. The landlord never sees enum names — only plain questions.
 */
const SOURCE_CLARIFICATION: Record<
  'substantive_rental_history' | 'substantive_employment',
  { testId: string; question: string; selfLabel: string; serviceLabel: string }
> = {
  substantive_rental_history: {
    testId: 'rental-history-source-question',
    question: 'How did you obtain this rental history?',
    selfLabel: 'I contacted a prior landlord directly',
    serviceLabel: 'Through a tenant screening or background check service',
  },
  substantive_employment: {
    testId: 'employment-source-question',
    question: 'How did you obtain this employment or income information?',
    selfLabel: 'From documents the applicant provided (pay stubs, bank statements, employer letters)',
    serviceLabel: 'Through a third-party employment or income verification service',
  },
};

export function DecisionModal({ leadId, onClose, onSuccess }: DecisionModalProps) {
  const [decision, setDecision] = useState<LandlordSelectableLeadDecision | null>(null);
  const [rejectionBasis, setRejectionBasis] = useState<LeadRejectionBasis | null>(null);
  const [sourceAnswer, setSourceAnswer] = useState<'self' | 'service' | null>(null);
  const [decisionNote, setDecisionNote] = useState('');
  const [hasBlurredNote, setHasBlurredNote] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  const isRejected = decision === 'rejected';
  const isSubstantiveOther = rejectionBasis === 'substantive_other';

  // Bases where a follow-up question disambiguates self-obtained vs. via-a-service.
  const sourceClarification =
    rejectionBasis === 'substantive_rental_history' || rejectionBasis === 'substantive_employment'
      ? SOURCE_CLARIFICATION[rejectionBasis]
      : null;

  // The basis actually stored: "via a service" is a consumer report under FCRA.
  const effectiveBasis: LeadRejectionBasis | null = sourceClarification
    ? sourceAnswer === 'service'
      ? 'substantive_consumer_report'
      : sourceAnswer === 'self'
        ? rejectionBasis
        : null
    : rejectionBasis;

  const canSubmit = (
    decision !== null &&
    (!isRejected || effectiveBasis !== null) &&
    (!isSubstantiveOther || decisionNote.trim().length > 0)
  );

  // FM2-1: only show the note error after the user has interacted with the field (blur),
  // not pre-emptively on basis selection.
  const showNoteError = isRejected && isSubstantiveOther && decisionNote.trim().length === 0 && hasBlurredNote;

  const handleBasisChange = (value: LeadRejectionBasis) => {
    setRejectionBasis(value);
    setSourceAnswer(null);
    if (value !== 'substantive_other') setHasBlurredNote(false);
  };

  useEffect(() => {
    const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key !== 'Tab') return;
      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last?.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first?.focus(); }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    const dialog = dialogRef.current;
    if (dialog) {
      const first = dialog.querySelector<HTMLElement>(FOCUSABLE);
      first?.focus();
    }
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleDecisionChange = (value: LandlordSelectableLeadDecision) => {
    setDecision(value);
    // Reset rejection basis + source answer when decision changes away from rejected
    if (value !== 'rejected') {
      setRejectionBasis(null);
      setSourceAnswer(null);
      setHasBlurredNote(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!decision || !canSubmit) return;
    setError(null);
    setIsSubmitting(true);
    try {
      const result = await decideLead(leadId, {
        decision,
        decisionNote: decisionNote.trim() || undefined,
        closeSiblings: 'none',
        ...(isRejected && effectiveBasis ? { rejectionBasis: effectiveBasis } : {}),
      });
      onSuccess(result.siblingCount ?? 0, result.siblingIds ?? []);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to record decision');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      data-testid="decision-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="decision-modal-title"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div ref={dialogRef} className="bg-card rounded-lg border border-border w-full max-w-md p-6 shadow-lg max-h-[90vh] overflow-y-auto">
        <h2 id="decision-modal-title" className="text-lg font-semibold mb-4">
          Record Decision
        </h2>

        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4" data-testid="decision-form">
          {/* Decision radio group */}
          <fieldset>
            <legend className="block text-sm font-medium mb-2">
              Decision <span className="text-destructive" aria-hidden="true">*</span>
              <span className="sr-only"> (required)</span>
            </legend>
            <div className="space-y-2">
              {LANDLORD_DECISIONS.map((d) => (
                <label
                  key={d}
                  className="flex items-center gap-3 cursor-pointer group min-h-[44px]"
                >
                  <input
                    type="radio"
                    name="lead-decision"
                    value={d}
                    checked={decision === d}
                    onChange={() => handleDecisionChange(d)}
                    disabled={isSubmitting}
                    required
                    aria-required="true"
                    data-testid={`decision-option-${d}`}
                    className="h-4 w-4 text-primary border-input focus:ring-ring focus:ring-2 shrink-0"
                  />
                  <span className="text-sm text-foreground group-hover:text-primary transition-colors">
                    {DECISION_OPTION_LABELS[d] ?? DECISION_LABELS[d]}
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          {/* Conditional rejection basis section */}
          {isRejected && (
            <fieldset
              data-testid="rejection-basis-section"
              className="border border-border rounded-md p-3 space-y-2"
            >
              <legend className="text-sm font-medium px-1">
                Why are you rejecting? <span className="text-destructive" aria-hidden="true">*</span>
                <span className="sr-only"> (required)</span>
              </legend>
              {REJECTION_BASIS_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  className="flex items-start gap-3 cursor-pointer group min-h-[44px]"
                >
                  <input
                    type="radio"
                    name="rejection-basis"
                    value={opt.value}
                    checked={rejectionBasis === opt.value}
                    onChange={() => handleBasisChange(opt.value)}
                    disabled={isSubmitting}
                    required
                    aria-required="true"
                    data-testid={`rejection-basis-${opt.value}`}
                    className="h-4 w-4 text-primary border-input focus:ring-ring focus:ring-2 shrink-0 mt-0.5"
                  />
                  <span className="text-sm text-foreground group-hover:text-primary transition-colors">
                    {opt.label}
                  </span>
                </label>
              ))}

              {/* Source clarification — rental history / employment can be self-obtained
                  or via a third-party service (a consumer report under FCRA). */}
              {sourceClarification && (
                <fieldset
                  data-testid={sourceClarification.testId}
                  className="mt-2 ml-1 border-l-2 border-border pl-3 space-y-2"
                  role="radiogroup"
                  aria-required="true"
                >
                  <legend className="text-sm font-medium px-1">
                    {sourceClarification.question}{' '}
                    <span className="text-destructive" aria-hidden="true">*</span>
                    <span className="sr-only"> (required)</span>
                  </legend>
                  {([
                    { answer: 'self' as const, label: sourceClarification.selfLabel },
                    { answer: 'service' as const, label: sourceClarification.serviceLabel },
                  ]).map(({ answer, label }) => (
                    <label key={answer} className="flex items-start gap-3 cursor-pointer group min-h-[44px]">
                      <input
                        type="radio"
                        name="rejection-source"
                        value={answer}
                        checked={sourceAnswer === answer}
                        onChange={() => setSourceAnswer(answer)}
                        disabled={isSubmitting}
                        required
                        aria-required="true"
                        data-testid={`rejection-source-${answer}`}
                        className="h-4 w-4 text-primary border-input focus:ring-ring focus:ring-2 shrink-0 mt-0.5"
                      />
                      <span className="text-sm text-foreground group-hover:text-primary transition-colors">
                        {label}
                      </span>
                    </label>
                  ))}
                  {/* Persistent live region so the legally-significant consequence is
                      announced to AT the moment "via a service" is selected. */}
                  <div aria-live="polite" aria-atomic="true">
                    {sourceAnswer === 'service' && (
                      <p className="text-xs text-muted-foreground pt-1" data-testid="rejection-source-cra-hint">
                        Because this came from a screening or verification service, the notice will
                        include the consumer report disclosures required by law.
                      </p>
                    )}
                  </div>
                </fieldset>
              )}
            </fieldset>
          )}

          <div>
            <label htmlFor="lead-decision-note" className="block text-sm font-medium mb-1">
              Decision Note{' '}
              {isSubstantiveOther ? (
                <span className="text-destructive" aria-hidden="true">*</span>
              ) : (
                <span className="text-muted-foreground font-normal">(optional)</span>
              )}
            </label>
            <textarea
              id="lead-decision-note"
              data-testid="decision-note-input"
              value={decisionNote}
              onChange={(e) => setDecisionNote(e.target.value)}
              onBlur={() => setHasBlurredNote(true)}
              rows={3}
              placeholder={isSubstantiveOther ? 'Describe the reason for rejection…' : 'Add any notes about this decision…'}
              className="w-full border border-input rounded-md px-3 py-2 text-sm bg-background text-foreground outline-none focus:ring-2 focus:ring-ring resize-y"
              disabled={isSubmitting}
              required={isSubstantiveOther}
              aria-required={isSubstantiveOther}
              aria-invalid={showNoteError ? true : undefined}
              aria-describedby={showNoteError ? 'decision-note-error' : undefined}
            />
            {showNoteError && (
              <p id="decision-note-error" className="mt-1 text-xs text-destructive" role="alert">
                Describe the reason for rejection.
              </p>
            )}
          </div>

          {error && (
            <div
              data-testid="decision-modal-error"
              className="p-3 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm"
              role="alert"
            >
              {error}
            </div>
          )}

          {/* Explain why submit is blocked when the source question is the only gap. */}
          {sourceClarification && sourceAnswer === null && (
            <p id="source-clarification-required-hint" className="text-xs text-muted-foreground" data-testid="source-clarification-required-hint">
              Answer how you obtained this information to continue.
            </p>
          )}

          <div className="flex gap-3">
            <Button
              type="submit"
              data-testid="decision-modal-submit"
              disabled={isSubmitting || !canSubmit}
              aria-describedby={
                sourceClarification && sourceAnswer === null ? 'source-clarification-required-hint' : undefined
              }
              className="flex-1"
            >
              {isSubmitting ? 'Recording…' : 'Record Decision'}
            </Button>
            <Button
              type="button"
              variant="outline"
              data-testid="decision-cancel"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
